/**
 * Inline [n] citation rendering, shared by the SDS assistant and Customer 360.
 *
 * The backend already strips markers that point at nothing, but `maxId` is enforced here
 * as well: the two guards are independent, and a chip that scrolls to a card that does
 * not exist is worse than plain text.
 */
import { useMemo } from 'react';

/** Anchor scheme for citation links. Kept in step with the regex in `citationComponents`. */
const ANCHOR_PREFIX = '#ai-source-';

/**
 * Turns bare [n] markers into markdown links so react-markdown can render them as
 * clickable chips. Markers already followed by "(" are left alone — those are real
 * markdown links, not citations. Numbers above `maxId` stay plain text.
 */
export function linkifyCitations(markdown: string, maxId: number): string {
    if (maxId <= 0) return markdown;
    return markdown.replace(/\[(\d{1,2})\](?!\()/g, (match, digits: string) => {
        const n = Number(digits);
        if (n < 1 || n > maxId) return match;
        return `[${digits}](${ANCHOR_PREFIX}${digits})`;
    });
}

/**
 * Which source ids the text actually cites.
 *
 * Display only — the source LIST is always built from the retrieval, never from the
 * model's text. This just says which of those entries the answer leaned on, so a
 * question that retrieved five datasheets and cited none of them does not read as
 * though it was answered out of them.
 */
export function citedSourceIds(markdown: string, maxId: number): number[] {
    if (maxId <= 0) return [];
    const found = new Set<number>();
    for (const m of markdown.matchAll(/\[(\d{1,2})\](?!\()/g)) {
        const n = Number(m[1]);
        if (n >= 1 && n <= maxId) found.add(n);
    }
    return [...found];
}

/**
 * `components` for react-markdown that renders citation anchors as chips and leaves
 * every other link as a normal external link.
 */
export function useCitationComponents(onCitationClick: (sourceId: number) => void) {
    return useMemo(() => ({
        a: ({ href, children }: { href?: string; children?: React.ReactNode }) => {
            const match = href?.match(/^#ai-source-(\d+)$/);
            if (match) {
                const sourceId = Number(match[1]);
                return (
                    <button
                        type="button"
                        // The chip sits inside a message bubble that is itself clickable in
                        // Customer 360. Without this, the bubble's handler runs after the
                        // chip's and resets the highlight the chip just set — the chip would
                        // silently do nothing.
                        onClick={e => { e.stopPropagation(); onCitationClick(sourceId); }}
                        className="mx-0.5 inline-flex items-center rounded bg-blue-100 px-1.5 py-0 align-baseline text-[11px] font-semibold text-blue-700 hover:bg-blue-200"
                        title={`Show source ${sourceId}`}
                    >
                        {children}
                    </button>
                );
            }
            return (
                <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    {children}
                </a>
            );
        },
    }), [onCitationClick]);
}
